import { useState, useEffect, FC } from 'react'
import {
  useDiamondData,
  useDiamondFilterState,
} from 'context/diamondFilterContext'
import {
  DiamondDataContextType,
  DiamondFilterContextType,
} from 'types/diamondFilter.types'
import {
  fetchDiamonds,
  generateFilterSlug,
  generateSEOTitle,
  generateSEODescription,
} from './helpers'
import { useRouter } from 'next/router'
import Head from 'next/head'
import { getCanonicalUrlForDiamondSearchPage } from '@utils/generate-canonical-url'
import { SEO } from '@components/common'
import dynamic from 'next/dynamic'

interface Props {
  className?: string
}

const DiamondShapeSelector = dynamic(
  () => import('@components/yadav/ui/DiamondShapeSelector')
)

const PriceRangeSlider = dynamic(
  () => import('@components/yadav/ui/PriceRangeSlider')
)

const CaratsRangeSlider = dynamic(
  () => import('@components/yadav/ui/CaratsRangeSlider')
)

const ExcellentCutFilter = dynamic(
  () => import('@components/yadav/ui/CutRangeSlider/ExcellentCutFilter')
)

const ColorRangeSlider = dynamic(
  () => import('@components/yadav/ui/ColorRangeSlider')
)

const ClarityRangeSlider = dynamic(
  () => import('@components/yadav/ui/ClarityRangeSlider')
)

const FilterGridButtons = dynamic(
  () => import('@components/yadav/ui/FilterGridButtons')
)

const AdvancedFilters = dynamic(
  () => import('@components/yadav/ui/AdvancedFilters')
)

const CheckboxesSelectorNav = dynamic(
  () => import('@components/yadav/ui/CheckboxesSelectorNav')
)

const DiamondsFilter: FC<Props> = ({ className }) => {
  const router = useRouter()
  const { diamondFilterState } =
    useDiamondFilterState() as DiamondFilterContextType
  const { setLoadingDiamonds, setDiamondData, setDiamondMeta } =
    useDiamondData() as DiamondDataContextType
  const [canonicalUrl, setCanonicalUrl] = useState<string | null>(null)
  const [seoTitle, setSEOTitle] = useState('')
  const [seoDescription, setSEODescription] = useState('')
  const [openTooltip, setOpenTooltip] = useState<
    'shape' | 'carats' | 'color' | 'clarity' | 'cut' | 'price' | null
  >(null)

  useEffect(() => {
    // Timeout necessary to avoid multiple requests when filters updates their state multiple times.
    const timer = setTimeout(() => {
      try {
        window.localStorage.setItem(
          'search-diamonds',
          JSON.stringify(diamondFilterState)
        )
        const params = generateFilterSlug(diamondFilterState)
        const { ring } = router.query
        if (
          router.pathname &&
          router.pathname.split('/')[1] === 'natural-loose-diamonds'
        ) {
          const slug = ring ? `${params}?ring=${ring}` : `${params}`
          window.history.replaceState(
            {
              ...window.history.state,
              as: `/natural-loose-diamonds/${slug}`,
              url: `/natural-loose-diamonds/${slug}`,
            },
            '',
            `/natural-loose-diamonds/${slug}`
          )
        } else if (
          router.pathname &&
          router.pathname.split('/')[1] === 'lab-created-diamonds'
        ) {
          const slug = ring ? `${params}?ring=${ring}` : `${params}`
          window.history.replaceState(
            {
              ...window.history.state,
              as: `/lab-created-diamonds/${slug}`,
              url: `/lab-created-diamonds/${slug}`,
            },
            '',
            `/lab-created-diamonds/${slug}`
          )
        }

        const title = generateSEOTitle(diamondFilterState)
        const description = generateSEODescription(diamondFilterState)

        setSEOTitle(title)
        setSEODescription(description)

        fetchDiamonds(diamondFilterState)
          .then((response) => {
            if (!response) {
              setDiamondData(null)
            } else {
              setDiamondData(response.data.map((diamond) => diamond.attributes))
              setDiamondMeta(response.meta)
            }
            setLoadingDiamonds(false)
          })
          .catch(() => {
            setDiamondData(null)
            setLoadingDiamonds(false)
          })
      } catch (error) {
        // If save to storage fails, the initial state is used instead.
      }
    }, 500)

    return () => {
      clearTimeout(timer)
    }
  }, [
    diamondFilterState,
    router.pathname,
    router.query,
    setDiamondData,
    setDiamondMeta,
    setLoadingDiamonds,
  ])

  useEffect(() => {
    const url = getCanonicalUrlForDiamondSearchPage(diamondFilterState)
    setCanonicalUrl(url)
  }, [diamondFilterState])

  return (
    <>
      <Head>
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      </Head>
      <SEO title={seoTitle} description={seoDescription} />
      <div className={className}>
        <>
          <FilterGridButtons className="w-full md:hidden mt-6 mb-6" />
          <div className="hidden md:block md:mb-3">
            <div className="grid grid-cols-2 grid-rows-[auto_auto_auto] gap-8 px-3">
              <DiamondShapeSelector
                className="hidden md:flex md:flex-wrap w-full mx-auto"
                openTooltip={openTooltip}
                setOpenTooltip={setOpenTooltip}
              />
              <ExcellentCutFilter
                openTooltip={openTooltip}
                setOpenTooltip={setOpenTooltip}
              />
              <CaratsRangeSlider
                className="hidden md:block w-full mx-auto"
                openTooltip={openTooltip}
                setOpenTooltip={setOpenTooltip}
              />
              <PriceRangeSlider
                className="hidden md:block w-full mx-auto"
                openTooltip={openTooltip}
                setOpenTooltip={setOpenTooltip}
              />
              <ColorRangeSlider
                className="hidden md:block w-full mx-auto"
                openTooltip={openTooltip}
                setOpenTooltip={setOpenTooltip}
              />
              <ClarityRangeSlider
                className="hidden md:block w-full h-full mx-auto"
                openTooltip={openTooltip}
                setOpenTooltip={setOpenTooltip}
              />
            </div>
          </div>
          <AdvancedFilters className="hidden md:flex flex-row mr-7 items-center" />
        </>
      </div>
    </>
  )
}

export default DiamondsFilter
